import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

import type {
  ApiKeyAudience,
  ApiKeyIdentityMode,
  ApiKeyResponse,
  CreateApiKeyBody,
  CreateApiKeyResponse,
  UpdateApiKeyBody,
} from '../lib/api-client';

export type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

export function useApiKeys() {
  const apiClient = useApiClient();
  const [keys, setKeys] = useState<ApiKeyResponse[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [error, setError] = useState<string | undefined>();
  // True when the listing is the tenant-subtree view (full_access callers):
  // keys across descendant organizations, each carrying its owning tenant.
  const [subtree, setSubtree] = useState(false);

  const refresh = useCallback(async () => {
    setStatus('loading');
    setError(undefined);
    try {
      // Try the subtree view first; the server 403s it for non-full_access
      // callers, in which case fall back to the creator-owned list. This keeps
      // the client free of capability plumbing.
      let list: ApiKeyResponse[];
      try {
        list = await apiClient.listApiKeys('subtree');
        setSubtree(true);
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 403) {
          list = await apiClient.listApiKeys();
          setSubtree(false);
        } else {
          throw err;
        }
      }
      setKeys(list);
      setStatus('success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load API keys';
      setError(msg);
      setStatus('error');
    }
  }, [apiClient]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createKey = useCallback(
    async (body: CreateApiKeyBody): Promise<CreateApiKeyResponse> => {
      const minted = await apiClient.createApiKey(body);
      // Optimistic: prepend so the new key appears at the top immediately.
      setKeys((prev) => [
        {
          object: 'api_key' as const,
          id: minted.id,
          name: minted.name,
          audience: minted.audience,
          key_prefix: minted.key_prefix,
          key_hint: minted.key_hint,
          scopes: minted.scopes,
          acts_as: minted.acts_as,
          agent_id: minted.agent_id,
          role_id: minted.role_id,
          skill_ids: minted.skill_ids,
          revoked_at: minted.revoked_at,
          expires_at: minted.expires_at,
          last_used_at: minted.last_used_at,
          created_at: minted.created_at,
        },
        ...prev,
      ]);
      return minted;
    },
    [apiClient],
  );

  const updateKey = useCallback(
    async (id: string, body: UpdateApiKeyBody): Promise<ApiKeyResponse> => {
      const updated = await apiClient.updateApiKey(id, body);
      setKeys((prev) => prev.map((k) => (k.id === id ? updated : k)));
      return updated;
    },
    [apiClient],
  );

  const revokeKey = useCallback(
    async (id: string): Promise<void> => {
      const updated = await apiClient.revokeApiKey(id);
      setKeys((prev) => prev.map((k) => (k.id === id ? updated : k)));
    },
    [apiClient],
  );

  return { keys, status, error, subtree, refresh, createKey, updateKey, revokeKey };
}

export type {
  ApiKeyAudience,
  ApiKeyIdentityMode,
  ApiKeyResponse,
  CreateApiKeyResponse,
  CreateApiKeyBody,
  UpdateApiKeyBody,
};
